import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  Alert,
  Button,
  Select,
  Input,
  DataTable,
  Skeleton,
  DatePicker,
  EmptyState,
  ErrorBoundary,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef, DateRange } from '@bbpos/ui';
import { GLASS, GLASS_FILTER } from '@/constants/design-tokens';
import {
  useStockTransfers,
  useStockTransferStats,
  TransferStatusBadge,
  StKpi,
} from '@/features/stock-transfers';
import type { TransferListItem, TransferStatus } from '@/features/stock-transfers';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { useAuthStore } from '@/stores/use-auth-store';
import { calendarDate, calendarDay, storeDatePresets, storeTimezone } from '@/lib/store-clock';
import { ROLE_LEVELS } from '@/types/user';
import { locationService } from '@/services/location.service';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowsRightLeftIcon,
  ArrowRightIcon,
  ClockIcon,
  TruckIcon,
  CheckCircleIcon,
  PlusIcon,
  EyeIcon,
} from './icons';

// ─── Constants ──────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'IN_TRANSIT', label: 'In Transit' },
  { value: 'PARTIALLY_RECEIVED', label: 'Partially Received' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'PARTIALLY_ACCEPTED', label: 'Partially Accepted' },
  { value: 'RECEIVED', label: 'Received' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

// Explicit "no location scope" value for the location_id URL param. Without it,
// clearing the dropdown would just fall back to the header's location instead of
// widening to all locations.
const ALL_LOCATIONS = 'all';

// ─── Header ─────────────────────────────────────────────────────────────────

// Shared by the loaded, empty and failed states so all three keep the same
// chrome — the page below only swaps what sits under it.
function TransferListHeader({ isManager, onNew }: { isManager: boolean; onNew: () => void }) {
  return (
    <div className="relative overflow-hidden bg-gradient-to-r from-teal-600 via-teal-500 to-brand-600 rounded-2xl p-6 text-white shadow-[0_24px_60px_-24px_rgba(13,148,136,0.55)] mb-6 animate-fade-in">
      <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
      <div className="relative flex items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight text-white">Stock Transfers</h1>
          <p className="text-teal-100 mt-1">Move inventory between your locations</p>
        </div>
        {isManager && (
          <Button
            variant="secondary"
            onClick={onNew}
            className="bg-white/20 hover:bg-white/30 text-white border-white/30 shrink-0"
          >
            <PlusIcon className="h-4 w-4 mr-1.5" />
            New Transfer
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Content ────────────────────────────────────────────────────────────────

function StockTransferListContent() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);
  const profile = useAuthStore((s) => s.profile);

  const page = Number(searchParams.get('page') ?? '1');
  const status = searchParams.get('status') ?? '';
  const search = searchParams.get('search') ?? '';
  const locationParam = searchParams.get('location_id') ?? '';
  const from = searchParams.get('from') ?? '';
  const to = searchParams.get('to') ?? '';

  // The on-page Location dropdown owns this page's scope; the header's global
  // location is only the default when the dropdown hasn't been touched. Both the
  // list AND the KPI aggregate read this, so the cards always describe the scope
  // the dropdown is showing.
  const effectiveLocationId =
    locationParam === ALL_LOCATIONS ? undefined : locationParam || selectedLocationId;

  // The range is the store's calendar days (T20): the filtered store's zone,
  // else the business's — the same day the API filters on.
  const timeZone = storeTimezone(profile, effectiveLocationId);
  const dateRange: DateRange | undefined =
    from && to ? { from: calendarDay(from), to: calendarDay(to) } : undefined;

  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const isManager = roleLevel >= ROLE_LEVELS.MANAGER;

  // Locations for filter dropdown
  const { data: locationsData } = useQuery({
    queryKey: ['locations-list'],
    queryFn: () => locationService.list({ limit: 100, is_active: 'true' }),
    staleTime: 60_000,
  });
  const locations = locationsData?.data ?? [];

  // Debounce search
  const [searchInput, setSearchInput] = useState(search);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // The "/" shortcut focuses this element directly. Looking the input up by its
  // placeholder text would couple the shortcut to display copy, so any reword or
  // translation of the placeholder would silently stop the shortcut from firing.
  const searchFieldRef = useRef<HTMLInputElement>(null);

  const handleSearch = useCallback(
    (value: string) => {
      setSearchInput(value);
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          if (value) {
            next.set('search', value);
          } else {
            next.delete('search');
          }
          next.set('page', '1');
          return next;
        });
      }, 300);
    },
    [setSearchParams],
  );

  function setParam(key: string, value: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      next.set('page', '1');
      return next;
    });
  }

  function setPage(p: number) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(p));
      return next;
    });
  }

  function setDateRange(range: DateRange | undefined) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (range?.from && range?.to) {
        next.set('from', calendarDate(range.from));
        next.set('to', calendarDate(range.to));
      } else {
        next.delete('from');
        next.delete('to');
      }
      next.set('page', '1');
      return next;
    });
  }

  const { data, isLoading, isError, error, refetch } = useStockTransfers({
    page,
    limit: 20,
    status: (status as TransferStatus) || undefined,
    search: search || undefined,
    location_id: effectiveLocationId,
    start_date: from || undefined,
    end_date: to || undefined,
  });

  // Single aggregate request backs the KPI counts. Scoped to the same location as
  // the list so the cards can't describe a different store than the one selected.
  const { data: stats } = useStockTransferStats({ location_id: effectiveLocationId });

  useEffect(() => {
    if (error) {
      toast({ title: (error as Error).message, variant: 'error' });
    }
  }, [error, toast]);

  // Keyboard shortcuts
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === '/') {
        e.preventDefault();
        searchFieldRef.current?.focus();
      }
      if (e.key === 'n' && isManager) {
        e.preventDefault();
        navigate('/inventory/stock-transfers/new');
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navigate, isManager]);

  const transfers = data?.data ?? [];
  // Filtered list total — drives pagination and the empty state only. The KPI row
  // must stay filter-independent, so every card reads from the stats aggregate;
  // otherwise "Total" would drop below the status counts once a filter is applied.
  const total = data?.meta?.total ?? 0;
  const totalTransfers = stats?.total ?? 0;
  const pendingCount = stats?.by_status?.PENDING ?? 0;
  const inTransitCount = stats?.by_status?.IN_TRANSIT ?? 0;
  const completedCount = stats?.by_status?.COMPLETED ?? 0;

  const columns: ColumnDef<TransferListItem>[] = [
    {
      key: 'transfer_number',
      header: 'Transfer #',
      render: (_v, row) => (
        <button
          type="button"
          onClick={() => navigate(`/inventory/stock-transfers/${row.id}`)}
          className="text-sm text-teal-600 dark:text-teal-400 hover:underline tabular-nums text-left font-medium font-mono"
        >
          {row.transfer_number}
        </button>
      ),
    },
    {
      key: 'source_location_name',
      header: 'Route',
      render: (_v, row) => (
        <div className="flex items-center gap-1.5 text-sm">
          <span
            className="text-neutral-700 dark:text-neutral-300 font-medium truncate max-w-[120px]"
            title={row.source_location_name}
          >
            {row.source_location_name}
          </span>
          <ArrowRightIcon className="h-3.5 w-3.5 text-neutral-400 dark:text-neutral-500 flex-shrink-0" />
          <span
            className="text-neutral-700 dark:text-neutral-300 font-medium truncate max-w-[120px]"
            title={row.destination_location_name}
          >
            {row.destination_location_name}
          </span>
        </div>
      ),
    },
    {
      key: 'item_count',
      header: 'Items',
      numeric: true,
      render: (_v, row) => (
        <span className="text-sm text-neutral-700 dark:text-neutral-300 tabular-nums font-mono">
          {row.item_count}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (_v, row) => <TransferStatusBadge status={row.status} />,
    },
    {
      key: 'created_at',
      header: 'Created',
      render: (_v, row) => (
        <span className="text-sm text-neutral-600 dark:text-neutral-400 tabular-nums font-mono">
          {new Date(row.created_at).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (_v, row) => (
        <button
          type="button"
          onClick={() => navigate(`/inventory/stock-transfers/${row.id}`)}
          className="p-1.5 rounded-lg text-neutral-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors"
          title="View"
          aria-label={`View transfer ${row.transfer_number}`}
        >
          <EyeIcon className="h-4 w-4" />
        </button>
      ),
    },
  ];

  // ── Loading skeleton ──────────────────────────────────────────────────────

  if (isLoading && !data) {
    return (
      <>
        <Skeleton variant="rect" height={96} className="rounded-2xl mb-6" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={120} className="rounded-2xl" />
          ))}
        </div>
        <Skeleton variant="rect" height={56} className="rounded-2xl mb-4" />
        <Skeleton variant="rect" height={400} className="rounded-2xl" />
      </>
    );
  }

  // ── Load failure ──────────────────────────────────────────────────────────
  // MUST come before the empty state below. A failed list request leaves `total`
  // at 0 exactly like a tenant with no transfers, so without this branch a 404/501
  // renders as "No stock transfers yet" — a broken page wearing a benign
  // "create your first transfer" CTA. Retry re-runs the list query in place.

  if (isError) {
    return (
      <>
        <TransferListHeader
          isManager={isManager}
          onNew={() => navigate('/inventory/stock-transfers/new')}
        />

        <div className={`${GLASS} p-6 animate-fade-in`}>
          <Alert
            variant="danger"
            title="Failed to load transfers"
            description={
              error instanceof Error && error.message
                ? error.message
                : "We couldn't load your stock transfers. This is a problem reaching the server, not an empty list."
            }
            action={{ label: 'Retry', onClick: () => void refetch() }}
          />
        </div>
      </>
    );
  }

  // ── Empty state (no filters applied) ──────────────────────────────────────

  if (!isLoading && total === 0 && !status && !search && !from) {
    return (
      <>
        <TransferListHeader
          isManager={isManager}
          onNew={() => navigate('/inventory/stock-transfers/new')}
        />

        <EmptyState
          variant="page"
          icon={<ArrowsRightLeftIcon className="h-full w-full" />}
          title="No stock transfers yet"
          description="Create your first transfer to move inventory between locations seamlessly."
          action={
            isManager
              ? { label: 'Create Stock Transfer', onClick: () => navigate('/inventory/stock-transfers/new') }
              : undefined
          }
        />
      </>
    );
  }

  return (
    <>
      {/* Gradient header */}
      <TransferListHeader
        isManager={isManager}
        onNew={() => navigate('/inventory/stock-transfers/new')}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6">
        <StKpi
          title="Total Transfers"
          value={totalTransfers.toLocaleString('en-IN')}
          tone="teal"
          icon={<ArrowsRightLeftIcon className="h-5 w-5" />}
          delay={0}
        />
        <StKpi
          title="Pending"
          value={pendingCount.toLocaleString('en-IN')}
          tone="warning"
          icon={<ClockIcon className="h-5 w-5" />}
          delay={60}
        />
        <StKpi
          title="In Transit"
          value={inTransitCount.toLocaleString('en-IN')}
          tone="brand"
          icon={<TruckIcon className="h-5 w-5" />}
          delay={120}
        />
        <StKpi
          title="Completed"
          value={completedCount.toLocaleString('en-IN')}
          tone="success"
          icon={<CheckCircleIcon className="h-5 w-5" />}
          delay={180}
        />
      </div>

      {/* Filters — glass filter bar */}
      <div className={`${GLASS_FILTER} flex flex-wrap items-end gap-3 mb-4 p-3`}>
        <div className="w-64">
          <DatePicker
            variant="range"
            value={dateRange}
            onChange={(r) => setDateRange(r as DateRange | undefined)}
            presets={storeDatePresets(timeZone)}
            placeholder="Filter by date range"
          />
        </div>
        <div className="w-44">
          <Select
            options={STATUS_OPTIONS}
            value={status}
            onChange={(v) => setParam('status', typeof v === 'string' ? v : '')}
            placeholder="All Statuses"
          />
        </div>
        <div className="w-56">
          <Input
            ref={searchFieldRef}
            placeholder="Search transfer number..."
            value={searchInput}
            onChange={(v) => handleSearch(v)}
          />
        </div>
        {locations.length > 1 && (
          <div className="w-48">
            <Select
              options={[
                { value: '', label: 'All Locations' },
                ...locations.map((l) => ({ value: l.id, label: l.name })),
              ]}
              value={effectiveLocationId ?? ''}
              onChange={(v) =>
                setParam('location_id', (typeof v === 'string' && v) || ALL_LOCATIONS)
              }
              placeholder="All Locations"
            />
          </div>
        )}
      </div>

      {/* Data Table */}
      <div className={`${GLASS} animate-slide-up`}>
        <DataTable<TransferListItem>
          columns={columns}
          data={transfers}
          loading={isLoading}
          totalCount={total}
          currentPage={page}
          pageSize={20}
          onPageChange={setPage}
          emptyState={{
            title: 'No transfers match filters',
            description: 'Try adjusting your filters or date range',
          }}
        />
      </div>
    </>
  );
}

// ─── Page export ────────────────────────────────────────────────────────────

export function StockTransferListPage() {
  return (
    <PageContainer
      title="Stock Transfers"
      breadcrumbs={[
        { label: 'Inventory', href: '/inventory' },
        { label: 'Stock Transfers' },
      ]}
    >
      <ErrorBoundary>
        <StockTransferListContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
